import {
  IsOptional,
  IsInt,
  Min,
  IsString,
  IsNumber,
  IsEnum,
  IsDateString,
} from 'class-validator';
import { ApiPropertyOptional } from '@nestjs/swagger';

/**
 * Estados disponibles para liquidaciones VD
 */
/* eslint-disable no-unused-vars */
export enum LiquidacionVdEstado {
  PENDIENTE = 'pendiente',
  PROCESADA = 'procesada',
  PAGADA = 'pagada',
  ANULADA = 'anulada',
}

/**
 * Tipos de envío disponibles
 */
export enum TipoEnvio {
  AEREO = 'aereo',
  MARITIMO = 'maritimo',
  TERRESTRE = 'terrestre',
  MULTIMODAL = 'multimodal',
}

/**
 * Coberturas de seguro disponibles
 */
export enum CoberturaSeguro {
  COMPLETA = 'completa',
  PARCIAL = 'parcial',
  SIN_SEGURO = 'sin_seguro',
}
/* eslint-enable no-unused-vars */

/**
 * DTO para filtrar la búsqueda de liquidaciones VD (Valor Declarado).
 * Permite filtrar por cliente, temporada, valor declarado, tipo de envío, paginación y ordenamiento.
 *
 * Uso: Se utiliza en el endpoint GET /api/v1/liquidaciones-vd para filtrar resultados.
 */
export class LiquidacionVdFilterDto {
  /**
   * ID del cliente a filtrar. Si no se especifica, depende del rol del usuario.
   * - Cliente: solo puede ver su propio ID.
   * - Superuser: puede seleccionar cualquier cliente.
   */
  @ApiPropertyOptional({
    description: 'ID del cliente',
    example: 66,
  })
  @IsOptional()
  @IsInt()
  clienteId?: number;

  /**
   * Temporada a consultar (ejemplo: '2024-2025').
   * Por defecto, se usa la temporada actual.
   * NOTA: Si se envían dateFrom/dateTo, este parámetro se ignora.
   */
  @ApiPropertyOptional({
    description:
      'ID de la temporada (ej: 7 para 2024-2025, 6 para 2023-2024). Ignorado si se usan dateFrom/dateTo',
    example: 7,
    type: Number,
  })
  @IsOptional()
  @IsInt()
  temporada?: number;

  /**
   * Fecha de inicio para filtrar liquidaciones (YYYY-MM-DD).
   * Si se especifica junto con dateTo, tiene prioridad sobre temporada.
   */
  @ApiPropertyOptional({
    description: 'Fecha de inicio del rango de búsqueda (formato: YYYY-MM-DD)',
    example: '2024-11-01',
    type: String,
  })
  @IsOptional()
  @IsDateString()
  dateFrom?: string;

  /**
   * Fecha de fin para filtrar liquidaciones (YYYY-MM-DD).
   * Si se especifica junto con dateFrom, tiene prioridad sobre temporada.
   */
  @ApiPropertyOptional({
    description: 'Fecha de fin del rango de búsqueda (formato: YYYY-MM-DD)',
    example: '2024-11-30',
    type: String,
  })
  @IsOptional()
  @IsDateString()
  dateTo?: string;

  /**
   * Valor mínimo declarado a filtrar.
   * Solo muestra liquidaciones con valor declarado >= valor especificado.
   */
  @ApiPropertyOptional({ description: 'Valor mínimo declarado a filtrar' })
  @IsOptional()
  @IsNumber()
  @Min(0)
  valorMinimo?: number;

  /**
   * Valor máximo declarado a filtrar.
   * Solo muestra liquidaciones con valor declarado <= valor especificado.
   */
  @ApiPropertyOptional({ description: 'Valor máximo declarado a filtrar' })
  @IsOptional()
  @IsNumber()
  @Min(0)
  valorMaximo?: number;

  /**
   * Tipo de envío a filtrar.
   */
  @ApiPropertyOptional({
    description: 'Tipo de envío',
    enum: TipoEnvio,
    example: 'maritimo',
  })
  @IsOptional()
  @IsEnum(TipoEnvio)
  tipoEnvio?: TipoEnvio;

  /**
   * Estado de la liquidación VD a filtrar.
   */
  @ApiPropertyOptional({
    description: 'Estado de la liquidación VD',
    enum: LiquidacionVdEstado,
    example: 'pagada',
  })
  @IsOptional()
  @IsEnum(LiquidacionVdEstado)
  estado?: LiquidacionVdEstado;

  /**
   * Filtro por cobertura de seguro.
   */
  @ApiPropertyOptional({
    description: 'Cobertura de seguro',
    enum: CoberturaSeguro,
    example: 'completa',
  })
  @IsOptional()
  @IsEnum(CoberturaSeguro)
  coberturaSeguro?: CoberturaSeguro;

  /**
   * Página de resultados (paginación).
   */
  @ApiPropertyOptional({
    description: 'Número de página',
    type: Number,
    example: 1,
  })
  @IsOptional()
  @IsInt()
  @Min(1)
  page?: number;

  /**
   * Cantidad de resultados por página.
   */
  @ApiPropertyOptional({
    description: 'Elementos por página',
    type: Number,
    example: 10,
  })
  @IsOptional()
  @IsInt()
  @Min(1)
  limit?: number;

  /**
   * Columna por la que se ordena la consulta.
   */
  @ApiPropertyOptional({
    description:
      "Columna para ordenar (ej: 'fechaLiquidacion', 'valorDeclarado', 'total')",
    example: 'fechaLiquidacion',
  })
  @IsOptional()
  @IsString()
  orderBy?: string;

  /**
   * Dirección de ordenamiento.
   */
  @ApiPropertyOptional({
    description: "Dirección de orden: 'ASC' o 'DESC'",
    enum: ['ASC', 'DESC'],
    example: 'DESC',
  })
  @IsOptional()
  @IsString()
  orderDirection?: 'ASC' | 'DESC';
}
